import { lazy, Suspense, useRef, useState, type ReactNode } from "react";
import type { ECharts } from "echarts/core";
import { ApiError } from "@/api/client";
import { useArtifactKinds } from "@/api/queries";
import { canSortByValue, sortedByValue } from "@/artifacts/sort";
import { printedControl } from "@/artifacts/controls";
import { ChartControl } from "./ChartControl";
import { FollowsChip } from "./LinkedScope";
import { useLinkedArtifact, useZoomedArtifact } from "./linked";
import { useInteractiveDocument } from "./documentMode";
import type { Artifact } from "@/artifacts/types";
import { Markdown } from "@/components/chat/Markdown";
import { Chip } from "@/components/ui/Chip";
import { EditableTitle } from "@/components/ui/EditableTitle";
import { Menu as DropdownMenu } from "@/components/ui/Menu";
import {
  AlertIcon,
  CardsIcon,
  ChartIcon,
  CopyIcon,
  DownloadIcon,
  NoteIcon,
  SortIcon,
  SwapIcon,
  TableIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";
import { SummaryCards } from "./SummaryCards";
import { TableArtifact } from "./TableArtifact";

// ECharts is most of the client bundle and an answer may have no chart at all,
// so it arrives as its own chunk the first time one is rendered rather than in
// the payload that has to paint the unlock page (PRD NFR-1).
const ChartCanvas = lazy(() =>
  import("./ChartCanvas").then((module) => ({ default: module.ChartCanvas })),
);

/**
 * The frame around one artifact: what it is, what had to be changed to render
 * it, and what a reader can do with it.
 *
 * The notes matter as much as the chart. When a spec could not be honoured —
 * a pie of negative numbers, a measure that turned out to be text, a tail
 * folded into "Other" — the server says so and it is shown here. A silent
 * degradation looks exactly like a bug in the data.
 *
 * The card knows nothing about *who owns* the artifact. An answer in the chat
 * workspace and a tile in a report grid are two owners of one vocabulary, so
 * the owner supplies the export URL, the swap action and any extra controls,
 * and there is exactly one renderer for all seventeen types (PRD FR-7.2).
 */

function icon(artifact: Artifact) {
  if (artifact.type === "table") return <TableIcon className="size-3.5" />;
  if (artifact.type === "summary_cards") return <CardsIcon className="size-3.5" />;
  if (artifact.type === "note") return <NoteIcon className="size-3.5" />;
  return <ChartIcon className="size-3.5" />;
}

function SwapMenu({
  artifact,
  onSwap,
  busy,
}: {
  artifact: Artifact;
  onSwap: (type: string) => void;
  busy: boolean;
}) {
  const { data: catalog } = useArtifactKinds();
  const options = artifact.swap_options ?? [];

  if (!options.length) return null;
  const label = (type: string) =>
    catalog?.kinds.find((kind) => kind.type === type)?.label ?? type.replace(/_/g, " ");

  return (
    <DropdownMenu
      label="Change chart type"
      className="min-w-40"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          disabled={busy}
          className={cn(
            "type-caption text-ink-secondary hover:text-ink hover:bg-hover motion-fast",
            "rounded-inner inline-flex items-center gap-1 px-2 py-1 disabled:opacity-55",
          )}
        >
          <SwapIcon className="size-3.5" />
          {label(artifact.type)}
        </button>
      )}
    >
      {(close) =>
        options.map((type) => (
          <button
            key={type}
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onSwap(type);
            }}
            className="type-support text-ink hover:bg-hover rounded-inner motion-fast px-2 py-1.5 text-start"
          >
            {label(type)}
          </button>
        ))
      }
    </DropdownMenu>
  );
}

export function ArtifactCard({
  artifact,
  csvUrl,
  onSwap,
  swapping = false,
  /** Owner-supplied controls in the header — a tile's menu, for instance. */
  actions,
  /** Shown under the header: a connection badge, a refreshed-at stamp. */
  subtitle,
  /** Lets a tile fill the height its grid frame gives it. */
  fillChart = false,
  /**
   * The dense reading of an artifact that lives in a fixed frame.
   *
   * Today only `summary_cards` reads differently: a bordered KPI box inside
   * this bordered card is a frame around a frame, and in a one-row tile it is
   * the 50px that cuts the number in half. See `SummaryCards`.
   */
  compact = false,
  /**
   * The interactive furniture: the swap menu and the export footer.
   *
   * Off for the print routes (PRD FR-9.1). A PDF with a "Copy" button drawn
   * on it is a PDF that lies about what a reader can do — and the same rule
   * that makes this one component serve chat and tiles makes it serve print,
   * rather than a third renderer that would drift from both.
   */
  chrome = true,
  /**
   * Makes the heading renameable in place. Tiles pass it; a chat artifact does
   * not, because its title belongs to the answer that produced it.
   */
  onRename,
  renameLabel = "artifact",
  /**
   * This artifact's position in the linked scope around it (LINK-2): an
   * answer's artifact index, a grid's tile order. Left out, the card ignores
   * any scope and renders exactly as it always has.
   */
  linkIndex,
  className,
  bodyClassName,
}: {
  artifact: Artifact;
  csvUrl: string;
  onSwap?: (type: string) => Promise<unknown>;
  swapping?: boolean;
  onRename?: (title: string) => Promise<unknown>;
  renameLabel?: string;
  actions?: ReactNode;
  subtitle?: ReactNode;
  fillChart?: boolean;
  compact?: boolean;
  chrome?: boolean;
  linkIndex?: number;
  className?: string;
  bodyClassName?: string;
}) {
  const chart = useRef<ECharts | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [byValue, setByValue] = useState(false);
  // A filter control (VIZ-3) works wherever a reader's browser is: in the app
  // and in the HTML file and embed. Only a printed page shows its default.
  const interactiveDocument = useInteractiveDocument();
  const live = chrome || interactiveDocument;
  // A linked selection (LINK-2): a narrowed chart arrives from the server and
  // stands in for this one; a highlight is drawn on whatever is shown.
  const linked = useLinkedArtifact(artifact, live ? linkIndex : undefined);
  const reaction = linked?.reaction ?? null;
  // A map drilled into one region (LINK-MAP): it applies only while that
  // region is still the selection, so a new pick or Escape goes back up.
  const [zoomKey, setZoomKey] = useState<string | null>(null);
  const zoomable = linked?.zoomable ?? null;
  const zoomed = zoomable && zoomKey === zoomable.key ? zoomable : null;
  const drilled = useZoomedArtifact(live ? linkIndex : undefined, zoomed);
  const source = linked?.view.artifact ?? artifact;
  const shownNotes = (drilled.artifact ?? source).notes ?? [];
  const control = source.control ?? null;
  const [picked, setPicked] = useState<string | null>(null);
  const current =
    control && picked && (control.values ?? []).includes(picked) ? picked : (control?.default ?? "");
  const sliced =
    control && live
      ? {
          ...source,
          chart:
            (source.slices ?? []).find((entry) => entry.value === current)?.chart ?? source.chart,
        }
      : source;
  const sortable = chrome && canSortByValue(sliced);
  const ordered = sortable && byValue ? sortedByValue(sliced) : sliced;
  const drawn = drilled.artifact
    ? drilled.artifact
    : reaction?.kind === "highlight" && ordered.chart
      ? { ...ordered, chart: { ...ordered.chart, highlight: reaction.marks } }
      : ordered;
  const narrowing = reaction?.kind === "filter-chart";
  const served = Boolean(linked?.view.artifact);
  // A table the server narrowed (a scoped re-run) holds only the selection's
  // rows; one it did not is filtered in place.
  const tableRows =
    served || !reaction
      ? null
      : reaction.kind === "filter-rows"
        ? { rows: reaction.rows, mode: "filter" as const }
        : reaction.kind === "mark-rows"
          ? { rows: reaction.rows, mode: "mark" as const }
          : null;
  const fetching = Boolean(linked?.view.pending && linked.view.fetched);
  const refused = linked?.view.error ?? null;
  const [copied, setCopied] = useState(false);
  const isChart = Boolean(artifact.chart);
  const hasRows = Boolean(artifact.data?.rows?.length);

  function savePng() {
    const instance = chart.current;
    if (!instance) return;
    const url = instance.getDataURL({
      pixelRatio: 2,
      // The chart is drawn for a themed ground; exporting it onto transparency
      // would produce an image that is unreadable in half of them.
      backgroundColor: getComputedStyle(document.documentElement)
        .getPropertyValue("--ming-card")
        .trim(),
    });
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(artifact.title || artifact.type).toLowerCase().replace(/\W+/g, "-")}.png`;
    link.click();
  }

  async function copyRows() {
    const data = artifact.data;
    if (!data) return;
    const lines = [
      (data.columns ?? []).map((column) => column.label).join("\t"),
      ...(data.rows ?? []).map((row) =>
        row.map((cell) => (cell === null ? "" : String(cell))).join("\t"),
      ),
    ];
    await navigator.clipboard.writeText(lines.join("\n"));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <section
      className={cn(
        "bg-card border-border rounded-container flex flex-col overflow-hidden border",
        className,
      )}
    >
      <header className="border-border flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <span className="text-ink-secondary">{icon(artifact)}</span>
        {onRename && chrome ? (
          <EditableTitle
            as="h3"
            className="type-label text-ink"
            value={artifact.title}
            placeholder="Result"
            label={renameLabel}
            onSave={onRename}
          />
        ) : (
          <h3 className="type-label text-ink min-w-0 flex-1 truncate">
            {artifact.title || "Result"}
          </h3>
        )}
        {linked ? <FollowsChip linked={linked} /> : null}
        {artifact.from_query && !actions ? (
          <span className="type-caption text-ink-disabled">query {artifact.from_query}</span>
        ) : null}
        {sortable ? (
          <button
            type="button"
            aria-pressed={byValue}
            title={byValue ? "Show in the order the query returned" : "Sort the bars by value"}
            onClick={() => setByValue((current) => !current)}
            className={cn(
              "type-caption hover:bg-hover motion-fast rounded-inner inline-flex items-center gap-1 px-2 py-1",
              byValue ? "text-ink" : "text-ink-secondary hover:text-ink",
            )}
          >
            <SortIcon className="size-3.5" />
            {byValue ? "By value" : "As queried"}
          </button>
        ) : null}
        {onSwap && chrome ? (
          <SwapMenu
            artifact={artifact}
            busy={swapping}
            onSwap={(type) => {
              setFailure(null);
              // The server's own sentence, not a house one over the top of it:
              // a refused swap says *why* it does not fit (backlog item 11).
              // This card owns the failure, so the two swap mutations opt out
              // of the global toast rather than reporting it twice.
              void onSwap(type).catch((error: unknown) =>
                setFailure(
                  error instanceof ApiError
                    ? error.message
                    : "That chart kind does not fit this data.",
                ),
              );
            }}
          />
        ) : null}
        {actions}
      </header>

      {subtitle ? (
        <div className="border-border bg-surface border-b px-3 py-1.5">{subtitle}</div>
      ) : null}

      {zoomable ? (
        <div className="border-border flex flex-wrap items-center gap-x-2 gap-y-1 border-b px-3 py-1.5">
          {zoomed ? (
            <nav aria-label="Map level" className="type-support flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setZoomKey(null)}
                className="text-accent motion-fast hover:underline"
              >
                {zoomable.root}
              </button>
              <span aria-hidden className="text-ink-disabled">
                ›
              </span>
              <span aria-current="page" className="text-ink">
                {zoomable.region}
              </span>
              {drilled.pending ? (
                <span className="type-caption text-ink-secondary normal-case">
                  · drawing its {zoomable.label}…
                </span>
              ) : null}
            </nav>
          ) : (
            <button
              type="button"
              onClick={() => setZoomKey(zoomable.key)}
              className="type-support text-accent motion-fast hover:underline"
            >
              Show {zoomable.region}&rsquo;s {zoomable.label}
            </button>
          )}
          {zoomed && drilled.error ? (
            <span className="type-support text-warning">{drilled.error}</span>
          ) : null}
        </div>
      ) : null}

      {control && !drilled.artifact ? (
        <div className="border-border border-b px-3 py-2">
          {live ? (
            <ChartControl control={control} value={current} onChange={setPicked} />
          ) : (
            <p className="type-support text-ink-secondary">{printedControl(control)}</p>
          )}
        </div>
      ) : null}

      {artifact.type === "summary_cards" && linked?.active ? (
        <p className="border-border type-caption text-ink-secondary border-b px-3 py-1.5 normal-case">
          The whole answer — these figures do not follow the filter.
        </p>
      ) : null}

      {shownNotes.length ? (
        <ul className="border-border flex flex-col gap-1 border-b px-3 py-2">
          {shownNotes.map((note) => (
            <li
              key={note}
              className="type-caption text-ink-secondary flex items-start gap-1.5 normal-case"
            >
              <AlertIcon className="text-warning mt-px size-3 shrink-0" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {failure ? (
        <p className="bg-error-subtle text-error type-support px-3 py-2">{failure}</p>
      ) : null}
      {reaction?.kind === "filter-rows" &&
      artifact.data?.truncated &&
      !served &&
      !linked?.view.pending &&
      !refused ? (
        // A capped table filtered in place, where nothing can fetch the rest —
        // a share, an exported file. It says so rather than looking whole.
        <p className="border-border type-caption text-ink-secondary border-b px-3 py-1.5 normal-case">
          These rows were capped, so only the ones here are filtered — the full detail is in the
          live app.
        </p>
      ) : null}
      {fetching ? (
        <p className="border-border type-caption text-ink-secondary border-b px-3 py-1.5 normal-case">
          These rows were capped, so they are being fetched again for this selection…
        </p>
      ) : null}
      {refused ? (
        // Not an error the reader caused: the server saying why it would not
        // fetch more — a query that limits its rows, a joined result. The
        // rows in hand are still shown, and the filter bar offers to ask.
        <p className="bg-warning-subtle text-warning type-support border-border flex items-start gap-2 border-b px-3 py-2">
          <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
          <span>
            {refused}
            {artifact.data?.truncated ? " Only the rows already here are shown." : ""}
          </span>
        </p>
      ) : null}

      <div className={cn("min-h-0 flex-1", bodyClassName)}>
        {artifact.type === "summary_cards" ? (
          <SummaryCards artifact={artifact} compact={compact} />
        ) : null}
        {/* A filling chart takes the frame's height, with a floor: a tile one
            row tall is a deliberate choice, but a 60px chart is not a chart.
            Below the floor the tile's own body scrolls. */}
        {isChart ? (
          <div
            aria-busy={narrowing && linked?.view.pending ? true : undefined}
            className={cn(
              "motion-fast px-2 pt-3 pb-1 transition-opacity",
              fillChart && "h-full min-h-44",
              // The previous chart stays, dimmed, while the narrowed one loads.
              narrowing && linked?.view.pending && "opacity-45",
            )}
          >
            <Suspense
              fallback={<div className="bg-muted rounded-element h-[300px] animate-pulse" />}
            >
              <ChartCanvas
                artifact={drawn}
                instanceRef={chart}
                fill={fillChart}
                interactive={chrome}
                onMarkClick={linked?.onMark ?? undefined}
              />
            </Suspense>
          </div>
        ) : null}
        {artifact.type === "table" && hasRows ? (
          <TableArtifact
            artifact={source}
            paged={chrome}
            linked={tableRows}
            onRowClick={linked?.onRow ?? undefined}
          />
        ) : null}
        {artifact.type === "note" ? (
          <div className="px-3 py-3">
            <Markdown>{artifact.text || "—"}</Markdown>
          </div>
        ) : null}
      </div>

      {chrome && (hasRows || artifact.items?.length) ? (
        <footer className="border-border flex flex-wrap items-center gap-1 border-t px-2 py-1.5">
          <a
            href={csvUrl}
            className="type-caption text-ink-secondary hover:text-ink hover:bg-hover motion-fast rounded-inner inline-flex items-center gap-1 px-2 py-1"
          >
            <DownloadIcon className="size-3.5" />
            CSV
          </a>
          {isChart ? (
            <button
              type="button"
              onClick={savePng}
              className="type-caption text-ink-secondary hover:text-ink hover:bg-hover motion-fast rounded-inner inline-flex items-center gap-1 px-2 py-1"
            >
              <DownloadIcon className="size-3.5" />
              PNG
            </button>
          ) : null}
          {hasRows ? (
            <button
              type="button"
              onClick={() => void copyRows()}
              className="type-caption text-ink-secondary hover:text-ink hover:bg-hover motion-fast rounded-inner inline-flex items-center gap-1 px-2 py-1"
            >
              <CopyIcon className="size-3.5" />
              {copied ? "Copied" : "Copy"}
            </button>
          ) : null}
          <span className="flex-1" />
          {artifact.degraded_from ? (
            <Chip tone="warning">shown as {artifact.type.replace(/_/g, " ")}</Chip>
          ) : null}
        </footer>
      ) : null}
    </section>
  );
}
